import { PROGRAM_TYPES } from 'utils/program/helpers'
import { filterUserContextForCourse } from 'utils/lms/userContextHelpers'
import {
  GRADE_K2_TIERED_INTERVENTIONS,
  GRADE_35_TIERED_INTERVENTIONS,
  GRADE_68_TIERED_INTERVENTIONS,
  TICourseNameToProgramMap,
} from 'utils/program/types'
import { programs } from 'layers/content/v2/hooks/useCourseManager'
import { findCourseTreeNodeById } from 'utils/courseTreeHelpers'

/**
 * Determines which program to use for courseManager based on the current program type
 * @param {string} program - The program type from PROGRAM_TYPES enum
 * @returns {string|null} The corresponding program for courseManager or null if not found
 */
export const getProgramForCourseManager = program => {
  if (
    program === PROGRAM_TYPES.ELEMENTARY ||
    program === PROGRAM_TYPES.ELEMENTARY_TIER_2
  ) {
    return programs.elementaryTieredInterventions
  } else if (
    program === PROGRAM_TYPES.MIDDLE_SCHOOL ||
    program === PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2
  ) {
    return programs.middleSchoolTieredInterventions
  }
  return null
}

// Helper function to find a node in the course tree by courseNodeId
const findNode = (courseTreeNode, courseNodeId) => {
  return findCourseTreeNodeById(courseTreeNode, courseNodeId, {
    matchLeafNodeId: false,
    useStartsWith: false,
  })
}

// Helper function to find the correct leaf_node_id from courseManager
const findLeafNodeId = ({ courseManager, courseNodeId, courseName }) => {
  if (!courseManager?.courseTrees || !courseNodeId || !courseName) {
    return null
  }

  const courseTree = courseManager.courseTrees[courseName]

  if (!courseTree) {
    console.warn(`Course tree not found for course: ${courseName}`)
    console.log(
      'Available course trees:',
      Object.keys(courseManager.courseTrees),
    )
    return null
  }

  const courseTreeNode = findNode(courseTree, courseNodeId)

  if (!courseTreeNode) {
    console.warn(
      `Course node ${courseNodeId} not found in course tree for ${courseName}`,
    )
    return null
  }
  return courseTreeNode.leafNodeId
}

/**
 * Handles the mark complete functionality for tiered interventions
 * @param {Object} params - Parameters object
 * @param {string} params.courseNodeId - The course node ID
 * @param {Object} params.instructorData - The instructor data
 * @param {string} params.program - The program type
 * @param {Object} params.userContext - The user context
 * @param {Object} params.courseManager - The course manager
 * @param {string} params.registrationId - The registration ID
 * @param {Function} params.onMarkComplete - The mark complete function
 * @returns {Promise<void>}
 */
export const handleTieredInterventionToggleComplete = async ({
  courseNodeId,
  instructorData,
  program,
  userContext,
  courseManager,
  registrationId,
  onMarkComplete,
}) => {
  if (!courseNodeId || !instructorData) {
    return
  }

  // For tiered interventions, we need to access the correct program-specific data
  let relevantInstructorData = null

  // Check program-specific data first, then fall back to root level if needed
  if (
    program === PROGRAM_TYPES.ELEMENTARY ||
    program === PROGRAM_TYPES.ELEMENTARY_TIER_2
  ) {
    relevantInstructorData = instructorData?.K5SelDigital?.educator?.[0]
  } else if (
    program === PROGRAM_TYPES.MIDDLE_SCHOOL ||
    program === PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2
  ) {
    relevantInstructorData = instructorData?.MspDigital?.educator?.[0]
  } else {
    // Fallback to root level educator/siteEducator if program-specific data not found
    relevantInstructorData =
      instructorData?.educator?.[0] || instructorData?.siteEducator?.[0]
  }

  // Determine the course name and program key based on the program type
  let courseName = null
  let programKey = null

  if (
    program === PROGRAM_TYPES.ELEMENTARY ||
    program === PROGRAM_TYPES.ELEMENTARY_TIER_2
  ) {
    if (window.location.pathname.includes('grades-k-2')) {
      courseName = GRADE_K2_TIERED_INTERVENTIONS
      programKey = TICourseNameToProgramMap[GRADE_K2_TIERED_INTERVENTIONS]
    } else {
      courseName = GRADE_35_TIERED_INTERVENTIONS
      programKey = TICourseNameToProgramMap[GRADE_35_TIERED_INTERVENTIONS]
    }
  } else if (
    program === PROGRAM_TYPES.MIDDLE_SCHOOL ||
    program === PROGRAM_TYPES.MIDDLE_SCHOOL_TIER_2
  ) {
    courseName = GRADE_68_TIERED_INTERVENTIONS
    programKey = TICourseNameToProgramMap[GRADE_68_TIERED_INTERVENTIONS]
  }

  if (!courseName || !programKey) {
    return
  }

  // Filter the userContext to include only the specific course
  const filteredUserContext = filterUserContextForCourse(
    userContext,
    courseName,
    programKey,
  )

  // Find the correct leafNodeId from the courseManager courseTrees
  let leafNodeId = findLeafNodeId({
    courseManager,
    courseNodeId,
    courseName,
  })

  // Fallback: Check if there are lesson_nodes in the instructor data that might have the correct ID
  if (
    !leafNodeId &&
    relevantInstructorData?.lesson_nodes &&
    Array.isArray(relevantInstructorData.lesson_nodes)
  ) {
    // Try to find a lesson node that matches our courseNodeId
    const matchingLesson = relevantInstructorData.lesson_nodes.find(
      lesson =>
        lesson.course_node_id === courseNodeId ||
        lesson.leaf_node_id === courseNodeId,
    )
    if (matchingLesson?.leaf_node_id) {
      leafNodeId = matchingLesson.leaf_node_id
    }
  }

  // If we still don't have a leafNodeId, we can't proceed
  if (!leafNodeId) {
    console.error(
      `Could not find leaf_node_id for courseNodeId: ${courseNodeId} in course: ${courseName}`,
    )
    return
  }

  const payload = {
    registrationId: registrationId,
    leafNodeId: leafNodeId,
    progress: 1,
    userContextB64: btoa(JSON.stringify(filteredUserContext)),
  }

  try {
    await onMarkComplete(payload)
    return true
  } catch (error) {
    throw error
  }
}
